import { useField } from '@unform/core';
import { SelectOption } from 'contracts/Common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactSelect, {
  GroupBase,
  Props as SelectProps,
  SingleValue,
  ActionMeta,
  MultiValue,
} from 'react-select';
import * as S from './styles';

interface Props extends Omit<
  SelectProps<SelectOption, boolean, GroupBase<SelectOption>>,
  'onChange'
> {
  name: string;
  id?: string;
  label?: string;
  onChange?: (option: SelectOption | null) => void;
}

export const Select: React.FC<Props> = ({
  name,
  id,
  label,
  options,
  placeholder = ' ',
  onChange,
  ...rest
}) => {
  const selectRef = useRef<any>(null);

  const { fieldName, defaultValue, registerField, error } = useField(name);
  const [hasSelection, setHasSelection] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_lastUpdate, setLastUpdate] = useState<number>(
    new Date().getUTCMilliseconds()
  );

  const handleChange = useCallback(
    (
      newValue: SingleValue<SelectOption> | MultiValue<SelectOption>,
      _actionMeta: ActionMeta<SelectOption>
    ) => {
      if (rest.isMulti) {
        const firstOption = (newValue as MultiValue<SelectOption>)[0] || null;
        onChange && onChange(firstOption);
        setHasSelection((newValue as MultiValue<SelectOption>).length > 0);
      } else {
        const singleOption = (newValue as SingleValue<SelectOption>) || null;
        onChange && onChange(singleOption);
        setHasSelection(singleOption !== null);
      }
    },
    [onChange, rest.isMulti]
  );

  const LabelComponent = useCallback((): JSX.Element => {
    if (!label) return <></>;
    return <S.FieldLabel htmlFor={id || fieldName}>{label}</S.FieldLabel>;
  }, [fieldName, id, label]);

  const ErrorComponent = useCallback((): JSX.Element => {
    if (!error) return <></>;
    return <S.FieldError>{error}</S.FieldError>;
  }, [error]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: (ref: any) => {
        if (rest.isMulti) {
          return (
            ref?.state?.selectValue?.map((o: SelectOption) => o.value) || []
          );
        }
        return ref?.state?.selectValue?.[0]?.value || '';
      },
      setValue: (ref: any, value: SelectOption | SelectOption[]) => {
        ref?.setValue(value, 'select-option', value);
        setLastUpdate(new Date().getUTCMilliseconds());
      },
      clearValue: (ref: any) => {
        ref?.setValue(null, 'select-option', null);
        setLastUpdate(new Date().getUTCMilliseconds());
      },
    });
  }, [fieldName, registerField, rest.isMulti]);

  return (
    <S.Container>
      <LabelComponent />
      <ReactSelect
        className={hasSelection ? 'has-selection' : ''}
        ref={selectRef}
        id={id || fieldName}
        name={fieldName}
        options={options}
        styles={S.DefaultSelectStyle}
        placeholder={placeholder}
        onChange={handleChange}
        isMulti={rest.isMulti}
        defaultValue={defaultValue}
        {...rest}
      />
      <ErrorComponent />
    </S.Container>
  );
};
